import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Button, Link, Typography, useMediaQuery } from '@material-ui/core'
import { CheckCircleOutline, InfoOutlined, LocationOnRounded, EventRounded, LocalCarWashRounded } from '@material-ui/icons'
import { Elements, useStripe } from '@stripe/react-stripe-js'

import { settings } from '../../../../store/constants'
import { whatsappChatProps } from '../../../../components/whatsapp-chat'
import {
  context,
  useOrdersApi
} from '../../../../store'
import { simplifyAddress } from '../../../../lib/address'
import { getStripe } from '../../../../lib/stripe'
import { payLead, payDetail, chargesOnCompletion } from '../../../../lib/payment-copy'
import { browserHistory } from '../../../../services/history'
import { sameDaySlotLabel } from '../../../../lib/same-day'
import { finalizeOutcome, UNCONFIRMED_HEADING, UNCONFIRMED_TEXT } from '../../../../lib/checkout-outcome'

import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)


const CheckoutProcessingComponent = React.memo(() => {
  const classes = useStyles()

  const { handleProcessCheckout, handleProcessEmbeddedCheckout } = useOrdersApi()
  const stripe = useStripe()
  const [stripeSettled, setStripeSettled] = useState(false)
  useEffect(() => {
    let live = true
    getStripe().then(() => { if (live) setStripeSettled(true) })
    return () => { live = false }
  }, [])
  const { sessionId } = useParams();
  // The one embedded checkout (EMBEDDED_CHECKOUT) lands on
  // /checkout/success/embedded?setup_intent=seti_..., from the booking screen
  // after an in-page confirm or from Stripe's return_url after a redirect
  // (3DS, Link), and finalizes by SetupIntent rather than by session.
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const embedded = sessionId === 'embedded'
  const setupIntentId = embedded ? query.get('setup_intent') : null
  const redirectFailed = embedded && query.get('redirect_status') === 'failed'
  const isNarrow = useMediaQuery('(max-width:599px)')

  // Capture the order details from the finalize response so we can echo
  // them back as a confirmation panel. Redux state isn't populated here -
  // the Stripe redirect was a full page navigation so the booking slice
  // is empty by the time we mount. The API response is the only source.
  const [orderInfo, setOrderInfo] = useState(null)
  // pending until the finalize answers; 'booked', or 'unconfirmed' when it
  // failed (lib/checkout-outcome.js). "You're booked!" only once it is true
  // (referral audit, 25 Oct 2026: the page said booked and nothing was).
  const [outcome, setOutcome] = useState('pending')
  const settle = result => {
    setOutcome(finalizeOutcome(result))
    if (result) setOrderInfo(result)
  }

  // Guard so we only finalize once per mount even if `stripe` re-renders
  // before/after the Stripe.js promise resolves. The empty-deps version
  // captured a null `stripe` and never refired on slower mobile connections.
  const finalizedRef = useRef(false)
  useEffect(() => {
    if (finalizedRef.current) return
    if (embedded) {
      // A redirect that came back failed saved no card: back to the
      // unfinished-booking page, which says no payment was taken.
      if (redirectFailed || !setupIntentId) { browserHistory.replace('/checkout/cancel'); return }
      finalizedRef.current = true
      handleProcessEmbeddedCheckout(setupIntentId).then(settle)
      return
    }
    // Waits for Stripe.js to settle, loaded or not: a customer sent to the
    // hosted page because Stripe.js failed (lib/stripe.js) comes back here
    // with no Stripe instance, and finalizing never needed one.
    if (!(stripe || stripeSettled) || !sessionId) return
    finalizedRef.current = true
    const run = async () => {
      settle(await handleProcessCheckout(stripe, sessionId))
    }
    run()
  }, [stripe, stripeSettled, sessionId, handleProcessCheckout, embedded, redirectFailed, setupIntentId, handleProcessEmbeddedCheckout])

  if (outcome === 'unconfirmed') {
    return (
      <Box className={classes.container} data-testid="booking-unconfirmed">
        <Box className={classes.iconWrapper}>
          <InfoOutlined className={classes.icon} />
        </Box>
        <Typography className={classes.heading}>{UNCONFIRMED_HEADING}</Typography>
        <Typography className={classes.subheading}>{UNCONFIRMED_TEXT}</Typography>
        <Box className={classes.card}>
          <Typography className={classes.cardText}>
            You can also message us on{' '}
            <Link {...whatsappChatProps("Hi GoWashMyCar! I've just tried to book and the page said it couldn't confirm my booking.")} color="secondary" className={classes.link}>
              WhatsApp
            </Link>.
          </Typography>
        </Box>
        <Button component={NavLink} to="/" variant="contained" color="primary" className={classes.homeButton} disableElevation>
          Return to homepage
        </Button>
      </Box>
    )
  }

  if (outcome === 'pending') {
    return (
      <Box className={classes.container} data-testid="booking-confirming">
        <Typography className={classes.heading}>Confirming your booking…</Typography>
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.iconWrapper}>
        <CheckCircleOutline className={classes.icon} />
      </Box>
      <Typography className={classes.heading}>You’re booked!</Typography>
      <Typography className={classes.subheading}>We’ll message you on the day when your valeter sets off and again when they arrive.</Typography>

      {orderInfo && (
        <Box className={classes.recap}>
          {orderInfo.address && (
            <div className={classes.recapRow}>
              <LocationOnRounded className={classes.recapIcon} fontSize="small" />
              <span>{isNarrow ? simplifyAddress(orderInfo.address) : orderInfo.address}</span>
            </div>
          )}
          {(orderInfo.date || orderInfo.time_bucket) && (
            <div className={classes.recapRow}>
              <EventRounded className={classes.recapIcon} fontSize="small" />
              {orderInfo.anytime_today ? (
                // Same-day: the arrival window, not the stored "Anytime Today".
                <span>{sameDaySlotLabel()}</span>
              ) : (
                <span>
                  {orderInfo.date}
                  {orderInfo.date && orderInfo.time_bucket ? ' · ' : ''}
                  {orderInfo.time_bucket}
                </span>
              )}
            </div>
          )}
          {orderInfo.services && (
            <div className={classes.recapRow}>
              <LocalCarWashRounded className={classes.recapIcon} fontSize="small" />
              <span>{orderInfo.services}</span>
            </div>
          )}
        </Box>
      )}

      <Box className={classes.card}>
        <Typography className={classes.cardText}>
          {chargesOnCompletion()
            ? `${payLead()} ${payDetail()}`
            : 'Your card is charged shortly after your booking is made. We keep it on file to make repeat bookings easy, and Stripe holds it, so we never see or store the details.'}
        </Typography>
      </Box>
      <Box className={classes.card}>
        <Typography className={classes.cardTitle}>Need to make changes?</Typography>
        <Typography className={classes.cardText}>
          Message us on{' '}
          <Link {...whatsappChatProps("Hi GoWashMyCar! I'd like to make changes to my booking 📱")} color="secondary" className={classes.link}>
            WhatsApp
          </Link>{' '}
          to amend your order.
        </Typography>
      </Box>
      <Box className={classes.socialRow}>
        <Typography className={classes.socialText}>Follow us on</Typography>
        <Link href="https://www.facebook.com/gowashmycar?ref=hl" target="_blank" rel="noopener noreferrer" color="secondary" className={classes.link}>
          Facebook
        </Link>
      </Box>
      <Button
        component={NavLink}
        to="/"
        variant="contained"
        color="primary"
        className={classes.homeButton}
        disableElevation
      >
        Return to homepage
      </Button>
    </Box>
  )
})

export const CheckoutSuccess = React.memo(() => {

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <Elements stripe={getStripe()}>
      <CheckoutProcessingComponent/>
    </Elements>
  )
})
